import { BeforeSendFn } from "posthog-js";

import { appVersion } from "src/api/hosts";
import { uuidRegex } from "src/utils/uuidRegex";

const NUMERIC_SEGMENT = /^\d+$/;

// FE route segments whose next segment is a customer-named value
// (entity schema name, event type).
const CUSTOMER_NAMED_COLLECTIONS = new Set(["entities", "events"]);

export const normalizePagePath = (pathname: string): string => {
  const segments = pathname.split("/");
  return segments
    .map((segment, index) => {
      if (uuidRegex.test(segment) || NUMERIC_SEGMENT.test(segment)) {
        return ":id";
      }
      const parent = segments[index - 1] ?? "";
      if (CUSTOMER_NAMED_COLLECTIONS.has(parent)) {
        return ":param";
      }
      // Customer-named ids that are neither UUIDs nor numeric: the entity id
      // property at /entities/:schema/:id/:tab and the event payload id at
      // /events/:event_type/event/:event_id.
      if (parent === "event" || segments[index - 2] === "entities") {
        return ":id";
      }
      return segment;
    })
    .join("/");
};

type CapturedException = { type?: string; value?: string };

const firstException = (
  properties: Record<string, unknown> | undefined,
): CapturedException | undefined => {
  const exceptionList = properties?.["$exception_list"];
  return Array.isArray(exceptionList)
    ? (exceptionList[0] as CapturedException | undefined)
    : undefined;
};

const AXIOS_STATUS_IN_MESSAGE = /status code (\d+)/;

// Every AxiosError carries axios's internal stack trace, so PostHog's default
// stack-trace grouping would collapse all HTTP failures into a single issue.
// Group them by the page they happened on plus the response status instead;
// the failed request itself stays available in the http_* properties (set in
// src/utils/logger.ts) and in the backend logs. This runs in before_send so
// that autocaptured unhandled rejections get the same grouping as errors
// reported through logger.error.
export const fingerprintHttpFailures: BeforeSendFn = (event) => {
  if (!event || event.event !== "$exception") {
    return event;
  }
  const exception = firstException(event.properties);
  if (exception?.type !== "AxiosError") {
    return event;
  }
  const status =
    event.properties.http_status ??
    event.properties.http_error_code ??
    exception.value?.match(AXIOS_STATUS_IN_MESSAGE)?.[1] ??
    "unknown";
  const page = normalizePagePath(window.location.pathname);
  event.properties.$exception_fingerprint = `AxiosError ${status} ${page}`;
  return event;
};

// The release is read per event so it always matches the bundle that captured
// it. posthog.register would instead persist it in localStorage, where it goes
// stale across deploys and leaks between tabs running different bundles.
export const tagAppVersion: BeforeSendFn = (event) => {
  const release = appVersion();
  if (event && release) {
    event.properties.$app_version = release;
  }
  return event;
};
